import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaArrowLeft, FaMapMarkerAlt, FaStar, FaVenusMars, FaIdCard, FaBriefcase, FaUserPlus, FaHandshake, FaEnvelope, FaCheckCircle } from 'react-icons/fa';

function WorkerDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    const fetchWorkerDetails = async () => {
      try {
        setLoading(true);
        // Assuming /workers/:id returns the worker details including profile
        const response = await apiClient.get(`/workers/${id}`);
        setWorker(response.data);
      } catch (err) {
        setError('Failed to load worker details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkerDetails();
  }, [id]);

  const handleInvite = async () => {
    try {
      if (!worker || !worker.profile) return;
      await apiClient.post('/broker/roster/invite', { workerProfileId: worker.profile.id });
      showNotification('Invitation sent successfully!', 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to send invitation.', 'error');
    }
  };

  const handleHire = () => {
    // Navigate to hire worker page
    navigate(`/hire/${worker.id}`);
  };

  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;
  if (error) return <div className="text-center text-red-500 mt-10">{error}</div>;
  if (!worker) return <div className="text-center mt-10">Worker not found.</div>;

  const { profile } = worker;
  const averageRating = worker.rating || 0;
  
  const formatAddress = (location) => {
    if (!location) return 'Address not available';
    if (typeof location === 'string') return location;
    const parts = [];
    if (location.upazila) parts.push(location.upazila);
    if (location.district) parts.push(location.district);
    if (location.division) parts.push(location.division);
    return parts.length > 0 ? parts.join(', ') : 'Address not specified';
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button onClick={() => navigate(-1)} className="flex items-center text-blue-600 hover:underline mb-6">
        <FaArrowLeft className="mr-2" />
        Back
      </button>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-green-600 h-32"></div>
        <div className="px-8 pb-8">
          <div className="relative flex justify-between items-end -mt-12 mb-6">
            <div className="bg-white p-1 rounded-full">
              {profile?.picture ? (
                <img src={loadImage(profile.picture)} alt={profile.name} className="w-24 h-24 rounded-full object-cover border-4 border-white" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 border-4 border-white">
                  <FaUser size={40} />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-gray-800">{profile?.name || 'Unknown Name'}</h1>
            {profile?.User?.is_Identity_Verified && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <FaCheckCircle className="w-3 h-3 mr-1" />
                    Verified
                </span>
            )}
          </div>
          <div className="flex flex-wrap gap-4 text-gray-600 mb-6">
            <div className="flex items-center"><FaMapMarkerAlt className="mr-2" /> {formatAddress(profile?.location)}</div>
            {/* Phone and Email are intentionally excluded */}
            {profile?.gender && <div className="flex items-center"><FaVenusMars className="mr-2" /> {profile.gender}</div>}
            <div className="flex items-center text-yellow-600"><FaStar className="mr-2" /> {averageRating} Rating</div>
          </div>

          {user?.role === 'buyer' && (
            <div className="flex gap-3">
              <button
                onClick={handleHire}
                className="flex items-center bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-semibold shadow-md"
              >
                <FaHandshake className="mr-2" /> Hire Me
              </button>
              <button
                onClick={() => navigate('/messages', { state: { startChatWith: { id: profile.id, name: profile.name, picture: profile.picture } } })}
                className="flex items-center bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition font-semibold shadow-md"
              >
                <FaEnvelope className="mr-2" /> Message
              </button>
            </div>
          )}
          {user?.role === 'broker' && (
            <button
              onClick={handleInvite}
              className="flex items-center bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition font-semibold shadow-md"
            >
              <FaUserPlus className="mr-2" /> Invite to Roster
            </button>
          )}
        </div>

        {notification && (
          <div className={`mx-8 mb-6 p-4 rounded-lg text-white shadow transform transition-all duration-300 ease-in-out ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
            {notification.message}
          </div>
        )}

        <div className="border-t px-8 py-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Worker Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-500 mb-1">Skills</h3>
              <div className="flex flex-wrap gap-2">
                {worker.categories?.map((cat, idx) => (
                  <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">{cat.Name}</span>
                )) || 'No skills listed'}
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-500 mb-1">Experience</h3>
              <p>{worker.experience_year ? `${worker.experience_year} Years` : 'Not specified'}</p>
            </div>
            <div>
              <h3 className="font-medium text-gray-500 mb-1">Strength</h3>
              <p>{worker.strength || 'Not specified'}</p>
            </div>
            <div>
              <h3 className="font-medium text-gray-500 mb-1">Bio</h3>
              <p>{worker.bio || 'No bio available'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkerDetailsPage;
